import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Chip,
  Grid,
  LinearProgress,
  TextField as MuiTextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { AccountBalance, Money, LocalAtm, Groups } from "@mui/icons-material";
import FormDialog from "../ProjectActivity/FormDialog";
import { useForm } from "react-hook-form";
import { RHFTextField } from "../../../Shared/RHFTextField";
import { ProjectContext } from "../../../AppContext/ProjectContext";
import { AuthContext } from "../../../AppContext/AuthContext";
import { AccountContext } from "../../../AppContext/AccountContext";
import {
  createCorpusDisbursement,
} from "../../../Config/db/corpus-disbursement.operations";
import { saveTransaction } from "../../../Config/db/transaction.operations";
import {
  getActiveVendors,
  getActiveContracts,
} from "../../../Config/db/vendor.operations";
import { logProjectActivity } from "../../../Config/db/project-activity.operations";

export default function CorpusDisbursementForm({
  open,
  handleClose,
  onSuccess,
  existingDisbursements = [],
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      amount: "",
      chequeNumber: "",
      bankName: "",
      payee: "",
      remarks: "",
    },
  });
  const hfControl = { errors, register };
  const { projectInfo } = useContext(ProjectContext);
  const { userInfo } = useContext(AuthContext);
  const { primaryAccount } = useContext(AccountContext);

  const [paymentMode, setPaymentMode] = useState("cheque"); // "cheque" | "cash"
  const [cashSource, setCashSource] = useState("withdrawn"); // "withdrawn" | "resident"

  // ── Vendor & Contract pickers ──
  const [vendors, setVendors] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [loadingVendors, setLoadingVendors] = useState(false);
  const [loadingContracts, setLoadingContracts] = useState(false);

  useEffect(() => {
    if (!userInfo?.communityCode || !open) return;
    setLoadingVendors(true);
    getActiveVendors(userInfo.communityCode)
      .then(setVendors)
      .finally(() => setLoadingVendors(false));
  }, [userInfo?.communityCode, open]);

  useEffect(() => {
    if (!selectedVendor) {
      setContracts([]);
      setSelectedContract(null);
      return;
    }
    setLoadingContracts(true);
    getActiveContracts(selectedVendor.id)
      .then(setContracts)
      .finally(() => setLoadingContracts(false));
  }, [selectedVendor]);

  // Budget vs spent calculations
  const budget = Number(projectInfo?.estimatedBudget) || 0;
  const activeDisbursements = existingDisbursements.filter(
    (d) => d.status !== "cancelled"
  );
  const totalSpent = activeDisbursements.reduce(
    (s, d) => s + Number(d.amount || 0),
    0
  );
  const budgetRemaining = budget - totalSpent;
  const enteredAmount = Number(watch("amount")) || 0;
  const primaryBalance = Number(primaryAccount?.balance || 0);

  const onSubmit = async (data) => {
    if (!primaryAccount?.id) return;
    try {
      const amount = Number(data.amount);

      const isCash = paymentMode === "cash";
      const fullMode = isCash ? `cash-${cashSource}` : "cheque";

      // 1. Record the disbursement document
      await createCorpusDisbursement(
        {
          amount,
          chequeNumber: isCash ? "" : data.chequeNumber,
          bankName: isCash ? "" : data.bankName,
          payee: selectedVendor ? selectedVendor.name : data.payee,
          remarks: data.remarks,
          paymentMode: fullMode,
          createdBy: userInfo?.uid,
          accountId: isCash ? null : primaryAccount.id,
          // Vendor & contract linkage
          vendorId: selectedVendor?.id || null,
          vendorName: selectedVendor?.name || null,
          contractId: selectedContract?.id || null,
          contractTitle: selectedContract?.title || null,
        },
        userInfo?.communityCode,
        projectInfo?.id
      );

      // 2. Debit primary account for cheque AND cash-withdrawn.
      //    Both represent money leaving the bank account.
      //    Cash from residents never entered the bank — no debit.
      const vendorLabel = selectedVendor ? selectedVendor.name : data.payee || "disbursement";
      const shouldDebitPrimary = !isCash || cashSource === "withdrawn";

      if (shouldDebitPrimary) {
        const debitDesc = isCash
          ? `Cash withdrawal + payout — ${vendorLabel} (${projectInfo?.title || ""})`
          : `Cheque payout — ${vendorLabel} (${projectInfo?.title || ""})`;
        await saveTransaction(userInfo?.communityCode, primaryAccount.id, {
          amount: -amount, // negative = debit
          type: "debit",
          description: debitDesc,
        });
      }

      // 3. Log activity event
      const modeLabel = isCash
        ? (cashSource === "withdrawn" ? "cash — withdrawn from bank" : "cash — collected from residents")
        : "cheque";
      logProjectActivity({
        projectId: projectInfo?.id,
        communityCode: userInfo?.communityCode,
        userId: userInfo?.uid,
        userName: userInfo?.name || userInfo?.email,
        type: "disbursement",
        description: `Disbursement of ₹${amount.toLocaleString()} to ${vendorLabel} (${modeLabel})`,
        meta: { amount, payee: vendorLabel, paymentMode: fullMode },
      });

      reset();
      setSelectedVendor(null);
      setSelectedContract(null);
      setPaymentMode("cheque");
      setCashSource("withdrawn");
      if (onSuccess) onSuccess();
      handleClose();
    } catch (err) {
      console.error("Failed to create disbursement:", err);
    }
  };

  return (
    <FormDialog
      title="Record Vendor Payout"
      description={paymentMode === "cash"
        ? cashSource === "withdrawn"
          ? "Cash withdrawn from bank and paid to vendor. Bank balance already reflects this — no account debit needed."
          : "Cash collected directly from residents and paid to vendor. This money was never deposited — no account debit needed."
        : "Issue a cheque from the community bank account. This will debit the primary account balance."
      }
      open={open}
      handleClose={() => {
        reset();
        setSelectedVendor(null);
        setSelectedContract(null);
        setPaymentMode("cheque");
        setCashSource("withdrawn");
        handleClose();
      }}
      postiveButtonText={paymentMode === "cash" ? "Record Payment" : "Record Cheque"}
      postiveButtonCallback={handleSubmit(onSubmit)}
    >
      <Grid container spacing={2}>
        {/* Payment mode toggle */}
        <Grid item xs={12}>
          <ToggleButtonGroup
            value={paymentMode}
            exclusive
            onChange={(_, val) => val && setPaymentMode(val)}
            size="small"
            fullWidth
            sx={{ mb: 0.5 }}
          >
            <ToggleButton value="cheque" sx={{ textTransform: "none", fontWeight: 600, gap: 0.5 }}>
              <AccountBalance fontSize="small" /> Cheque
            </ToggleButton>
            <ToggleButton value="cash" sx={{ textTransform: "none", fontWeight: 600, gap: 0.5 }}>
              <Money fontSize="small" /> Cash
            </ToggleButton>
          </ToggleButtonGroup>

          {/* Cash source sub-selection */}
          {paymentMode === "cash" && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block" }}>
                Where did this cash come from?
              </Typography>
              <ToggleButtonGroup
                value={cashSource}
                exclusive
                onChange={(_, val) => val && setCashSource(val)}
                size="small"
                fullWidth
              >
                <ToggleButton value="withdrawn" sx={{ textTransform: "none", fontSize: "0.8rem", gap: 0.5 }}>
                  <LocalAtm fontSize="small" /> Withdrawn from bank
                </ToggleButton>
                <ToggleButton value="resident" sx={{ textTransform: "none", fontSize: "0.8rem", gap: 0.5 }}>
                  <Groups fontSize="small" /> Collected from residents
                </ToggleButton>
              </ToggleButtonGroup>
              <Alert severity="info" sx={{ borderRadius: 2, mt: 1, py: 0 }} icon={false}>
                {cashSource === "withdrawn"
                  ? "Bank balance already reflects this withdrawal — no account debit will be recorded."
                  : "This cash was never deposited to the bank — no account debit will be recorded."
                }
              </Alert>
            </Box>
          )}
        </Grid>

        {/* Budget vs Spent summary */}
        {budget > 0 && (
          <Grid item xs={12}>
            <Box sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50", mb: 0.5 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Budget: ₹{budget.toLocaleString()}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  color={budgetRemaining >= 0 ? "success.main" : "error.main"}
                >
                  Remaining: ₹{budgetRemaining.toLocaleString()}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min((totalSpent / budget) * 100, 100)}
                color={totalSpent > budget ? "error" : "primary"}
                sx={{ height: 8, borderRadius: 4, mb: 1 }}
              />
              <Box
                sx={{ display: "flex", justifyContent: "space-between" }}
              >
                <Typography variant="caption" color="text.secondary">
                  Spent: ₹{totalSpent.toLocaleString()} (
                  {activeDisbursements.length} payout
                  {activeDisbursements.length !== 1 ? "s" : ""})
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {budget > 0
                    ? `${Math.round((totalSpent / budget) * 100)}%`
                    : ""}
                </Typography>
              </Box>
            </Box>
            {totalSpent + enteredAmount > budget && enteredAmount > 0 && (
              <Alert
                severity="warning"
                sx={{ borderRadius: 2, mt: 0.5, mb: 0.5 }}
              >
                This payout will exceed the estimated budget by ₹
                {(totalSpent + enteredAmount - budget).toLocaleString()}
              </Alert>
            )}
          </Grid>
        )}

        {/* Primary account balance hint (only for cheque payments) */}
        {paymentMode === "cheque" && (
          <Grid item xs={12}>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Chip
                size="small"
                label={`Primary balance: ₹${primaryBalance.toLocaleString()}`}
                color="primary"
                variant="outlined"
              />
              {enteredAmount > primaryBalance && (
                <Chip
                  size="small"
                  label="Exceeds available balance"
                  color="error"
                  variant="outlined"
                />
              )}
            </Box>
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <RHFTextField
            required="Amount is required"
            fullWidth
            name="amount"
            label="Amount (₹)"
            type="number"
            {...hfControl}
          />
        </Grid>
        {paymentMode === "cheque" && (
          <Grid item xs={12} sm={6}>
            <RHFTextField
              fullWidth
              name="chequeNumber"
              label="Cheque / Reference Number"
              {...hfControl}
            />
          </Grid>
        )}
        {paymentMode === "cheque" && (
          <Grid item xs={12} sm={6}>
            <RHFTextField
              fullWidth
              name="bankName"
              label="Bank / Account Name"
              {...hfControl}
            />
          </Grid>
        )}

        {/* ── Vendor & Contract selection ── */}
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={vendors}
            getOptionLabel={(v) => `${v.name}${v.category ? ` (${v.category})` : ""}`}
            value={selectedVendor}
            onChange={(_, val) => {
              setSelectedVendor(val);
              setSelectedContract(null);
            }}
            loading={loadingVendors}
            renderInput={(params) => (
              <MuiTextField {...params} label="Select Vendor" size="small" placeholder="Search vendor..." />
            )}
            isOptionEqualToValue={(opt, val) => opt.id === val?.id}
          />
        </Grid>
        {selectedVendor && (
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={contracts}
              getOptionLabel={(c) =>
                `${c.title}${c.amount ? ` — ₹${Number(c.amount).toLocaleString("en-IN")}` : ""}${c.frequency ? ` (${c.frequency})` : ""}`
              }
              value={selectedContract}
              onChange={(_, val) => setSelectedContract(val)}
              loading={loadingContracts}
              renderInput={(params) => (
                <MuiTextField {...params} label="Select Contract (optional)" size="small" placeholder="Search contract..." />
              )}
              isOptionEqualToValue={(opt, val) => opt.id === val?.id}
            />
          </Grid>
        )}
        {!selectedVendor && (
          <Grid item xs={12} sm={6}>
            <RHFTextField
              fullWidth
              name="payee"
              label="Payee (if no vendor selected)"
              {...hfControl}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <RHFTextField
            fullWidth
            name="remarks"
            label="Remarks"
            multiline
            rows={2}
            {...hfControl}
          />
        </Grid>
      </Grid>
    </FormDialog>
  );
}
